import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, TrendingUp, BarChart3, ClipboardList, UserPlus, Clock,
  GitCompare, Users, UserCog, CheckSquare, ListChecks, Activity, Briefcase,
  Calendar, MessageSquare, FileText, Mail, Settings, DollarSign, Shield,
  Search, Command, CornerDownLeft
} from 'lucide-react';

const RECENT_KEY = 'nimble_cmd_recent';
const MAX_RECENT = 5;

function getRecentPaths() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
  } catch { return []; }
}

function addRecentPath(path) {
  const recent = getRecentPaths().filter(p => p !== path);
  recent.unshift(path);
  localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}

const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const role = user.role || 'client';

  const allCommands = useMemo(() => {
    const shared = [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', keywords: 'home overview main' },
      { icon: CheckSquare, label: 'Tasks', path: '/tasks', keywords: 'todos assignments work' },
      { icon: Users, label: 'Team Directory', path: '/team', keywords: 'members staff people' },
      { icon: Briefcase, label: 'Timesheets', path: '/timesheets', keywords: 'hours attendance time clock' },
      { icon: Calendar, label: 'Leave Requests', path: '/leave', keywords: 'vacation pto time off' },
      { icon: Activity, label: 'Reports', path: '/reports', keywords: 'analytics data performance' },
      { icon: MessageSquare, label: 'Chat', path: '/chat', keywords: 'messages conversation' },
      { icon: UserCog, label: 'My Profile', path: '/profile', keywords: 'account settings preferences' },
      { icon: Settings, label: 'Settings', path: '/settings', keywords: 'configuration preferences' },
      { icon: Activity, label: 'Notification Preferences', path: '/notification-preferences', keywords: 'alerts email in-app notifications' },
    ];

    if (role === 'admin') {
      return [
        ...shared,
        { icon: TrendingUp, label: 'Analytics', path: '/admin/advanced-analytics', keywords: 'charts graphs metrics' },
        { icon: BarChart3, label: 'Advanced Reporting', path: '/admin/advanced-reporting', keywords: 'data export' },
        { icon: ClipboardList, label: 'VA Management', path: '/admin/va-management', keywords: 'virtual assistants manage' },
        { icon: UserPlus, label: 'VA Assignments', path: '/admin/va-assignments', keywords: 'assign match clients' },
        { icon: Clock, label: 'VA Scheduling', path: '/admin/va-scheduling', keywords: 'shifts schedule calendar' },
        { icon: Calendar, label: 'VA Availability', path: '/admin/va-availability', keywords: 'weekly calendar schedule working hours timezone' },
        { icon: Activity, label: 'VA Heatmap', path: '/admin/va-heatmap', keywords: 'performance hours worked heat map analytics' },
        { icon: GitCompare, label: 'VA Comparison', path: '/admin/va-comparison', keywords: 'compare ratings skills' },
        { icon: Shield, label: 'User Management', path: '/admin/users', keywords: 'admin users roles permissions' },
        { icon: ListChecks, label: 'Bulk Assign', path: '/admin/bulk-assign', keywords: 'batch tasks multiple' },
        { icon: DollarSign, label: 'Billing', path: '/billing', keywords: 'payments invoices subscription' },
        { icon: FileText, label: 'Weekly Digest', path: '/admin/weekly-digest', keywords: 'summary report email' },
        { icon: Mail, label: 'Email Templates', path: '/admin/email-templates', keywords: 'templates communications' },
      ];
    }

    if (role === 'va') {
      return [
        ...shared,
        { icon: DollarSign, label: 'My Earnings', path: '/earnings', keywords: 'payments salary income' },
      ];
    }

    // Client
    return [
      ...shared,
      { icon: DollarSign, label: 'Billing', path: '/billing', keywords: 'payments invoices' },
    ];
  }, [role]);

  const recentCommands = useMemo(() => {
    const paths = getRecentPaths();
    return paths.map(p => allCommands.find(c => c.path === p)).filter(Boolean);
  }, [allCommands, open]);

  const filtered = useMemo(() => {
    if (!query.trim()) return allCommands;
    const q = query.toLowerCase();
    return allCommands.filter(cmd =>
      cmd.label.toLowerCase().includes(q) || cmd.keywords.includes(q)
    );
  }, [query, allCommands]);

  // Reset active index when results change
  useEffect(() => { setActiveIndex(0); }, [filtered]);

  // Keyboard shortcut: Cmd+K or Ctrl+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return;
    const activeEl = listRef.current.children[activeIndex];
    if (activeEl) activeEl.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleSelect = useCallback((cmd) => {
    addRecentPath(cmd.path);
    setOpen(false);
    navigate(cmd.path);
  }, [navigate]);

  const handleKeyDown = (e) => {
    const totalItems = (!query.trim() && recentCommands.length > 0)
      ? recentCommands.length + filtered.length
      : filtered.length;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, totalItems - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (!query.trim() && recentCommands.length > 0) {
        if (activeIndex < recentCommands.length) {
          handleSelect(recentCommands[activeIndex]);
        } else {
          const allIdx = activeIndex - recentCommands.length;
          if (filtered[allIdx]) handleSelect(filtered[allIdx]);
        }
      } else if (filtered[activeIndex]) {
        handleSelect(filtered[activeIndex]);
      }
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]" data-testid="command-palette">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
      
      {/* Palette */}
      <div className="relative w-full max-w-lg mx-4 bg-white rounded-xl shadow-2xl border border-[#E5E7EB] overflow-hidden animate-in fade-in slide-in-from-top-4 duration-200">
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E7EB]">
          <Search className="w-5 h-5 text-[#9CA3AF] shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search pages..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 text-sm bg-transparent outline-none placeholder:text-[#9CA3AF]"
            data-testid="command-palette-input"
          />
          <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium text-[#9CA3AF] bg-[#F3F4F6] rounded border border-[#E5E7EB]">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-72 overflow-y-auto py-2" data-testid="command-palette-results">
          {filtered.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-[#9CA3AF]">
              No results found for "{query}"
            </div>
          ) : (
            <>
              {/* Recently Visited - only when no search query */}
              {!query.trim() && recentCommands.length > 0 && (
                <>
                  <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF]" data-testid="recent-section-header">
                    Recently Visited
                  </div>
                  {recentCommands.map((cmd, i) => {
                    const Icon = cmd.icon;
                    return (
                      <button
                        key={`recent-${cmd.path}`}
                        onClick={() => handleSelect(cmd)}
                        onMouseEnter={() => setActiveIndex(i)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                          i === activeIndex ? 'bg-[#5FD068]/10 text-[#0A0D0A]' : 'text-[#4B5563] hover:bg-[#F9FAFB]'
                        }`}
                        data-testid={`recent-item-${cmd.path.replace(/\//g, '-')}`}
                      >
                        <Icon className={`w-4 h-4 shrink-0 ${i === activeIndex ? 'text-[#5FD068]' : 'text-[#9CA3AF]'}`} />
                        <span className="flex-1 text-sm font-medium truncate">{cmd.label}</span>
                        <Clock className="w-3 h-3 text-[#D1D5DB] shrink-0" />
                        {i === activeIndex && (
                          <CornerDownLeft className="w-3.5 h-3.5 text-[#9CA3AF] shrink-0" />
                        )}
                      </button>
                    );
                  })}
                  <div className="mx-4 my-1.5 border-t border-[#F3F4F6]" />
                  <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF]">
                    All Pages
                  </div>
                </>
              )}
              {filtered.map((cmd, rawIdx) => {
                const i = (!query.trim() && recentCommands.length > 0) ? rawIdx + recentCommands.length : rawIdx;
                const Icon = cmd.icon;
                return (
                  <button
                    key={cmd.path}
                    onClick={() => handleSelect(cmd)}
                    onMouseEnter={() => setActiveIndex(i)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                      i === activeIndex ? 'bg-[#5FD068]/10 text-[#0A0D0A]' : 'text-[#4B5563] hover:bg-[#F9FAFB]'
                    }`}
                    data-testid={`command-item-${cmd.path.replace(/\//g, '-')}`}
                  >
                    <Icon className={`w-4 h-4 shrink-0 ${i === activeIndex ? 'text-[#5FD068]' : 'text-[#9CA3AF]'}`} />
                    <span className="flex-1 text-sm font-medium truncate">{cmd.label}</span>
                    {i === activeIndex && (
                      <CornerDownLeft className="w-3.5 h-3.5 text-[#9CA3AF] shrink-0" />
                    )}
                  </button>
                );
              })}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-4 px-4 py-2 border-t border-[#E5E7EB] bg-[#F9FAFB]">
          <div className="flex items-center gap-1 text-[10px] text-[#9CA3AF]">
            <kbd className="px-1 py-0.5 bg-white rounded border border-[#E5E7EB] font-mono">↑↓</kbd>
            <span>navigate</span>
          </div>
          <div className="flex items-center gap-1 text-[10px] text-[#9CA3AF]">
            <kbd className="px-1 py-0.5 bg-white rounded border border-[#E5E7EB] font-mono">↵</kbd>
            <span>select</span>
          </div>
          <div className="flex items-center gap-1 text-[10px] text-[#9CA3AF]">
            <kbd className="px-1 py-0.5 bg-white rounded border border-[#E5E7EB] font-mono">esc</kbd>
            <span>close</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
